import { LoadingOverlay, Title } from '@mantine/core';
import Breadcrumbs, { BreadcrumbItem } from 'components/BreadcrumbsComponent';
import ModuleManagementForm from 'components/forms/dacum/ModuleManagementForm';
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { pathnames } from 'routes/dacum';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { moduleManagementService } from 'services/dacum/module.service';
import { showSuccessNotification, showErrorNotification } from 'utils/notification';
import { useModuleManagementStore } from 'store/moduleManagement';
import {
  FormDataCreateCompetencyModule,
  FormDataUpdateCompetencyModule
} from 'types/dacum/moduleManagement';

const EditModuleManagementPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const { formData, setFormData, setMode } = useModuleManagementStore();

  const { data: moduleDetail, isLoading } = useQuery({
    queryKey: ['moduleDetail', id],
    queryFn: () => moduleManagementService.detailModule(id as string),
    enabled: !!id
  });

  useEffect(() => {
    if (id && moduleDetail) {
      setMode('edit');
      setFormData({
        trCompetencyModuleID: moduleDetail.data.trCompetencyModuleID,
        msCompetencyManagementID: moduleDetail.data.msCompetencyManagementID,
        msLCMSCourseID: moduleDetail.data.msLCMSCourseID,
        deliveryMode: moduleDetail.data.deliveryMode,
        liaType: moduleDetail.data.liaType,
        courseName: moduleDetail.data.courseName,
        dateIn: moduleDetail.data.dateIn,
        userIn: moduleDetail.data.userIn,
        dateUp: moduleDetail.data.dateUp,
        userUp: moduleDetail.data.userUp
      });
    }
  }, [id, moduleDetail, setFormData, setMode]);

  const breadcrumbs = [
    { title: 'Competency Module Management', href: pathnames.competencyModule },
    { title: 'Edit Module', href: '' }
  ];

  const createModule = useMutation({
    mutationFn: (data: FormDataCreateCompetencyModule) => {
      // This won't be used in edit mode, but required by the form component
      return moduleManagementService.createModule(data);
    },
    onSuccess: () => {
      // This won't be called in edit mode
    },
    onError: () => {
      // This won't be called in edit mode
    }
  });

  const updateModule = useMutation({
    mutationFn: (data: FormDataCreateCompetencyModule) => {
      const updateData: FormDataUpdateCompetencyModule = {
        ...data,
        trCompetencyModuleID: formData.trCompetencyModuleID as string
      };
      return moduleManagementService.updateModule(updateData);
    },
    onSuccess: () => {
      showSuccessNotification({
        title: 'Module Updated',
        message: 'Module has been updated successfully.'
      });
      queryClient.invalidateQueries({ queryKey: ['moduleCompetency'] });
      navigate(pathnames.competencyModule);
    },
    onError: (error) => {
      showErrorNotification({
        title: 'Failed to update module',
        message: error.message || 'Failed to update module, please try again later.'
      });
    }
  });

  return (
    <>
      <Breadcrumbs items={breadcrumbs as BreadcrumbItem[]} mb={32} />
      <Title order={2} mb={30}>
        Edit Module
      </Title>
      <ModuleManagementForm createModule={createModule} updateModule={updateModule} />
      <LoadingOverlay visible={isLoading} />
    </>
  );
};

export default EditModuleManagementPage;
